import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getActiveCustomerCart } from "../utils/getCart"

/**
 * Used for updating freight cost in the cart.
 * Uses setCustomField so only freightSurcharge is changed and all other custom fields
 * (e.g. orderNumber, orderText, deliveryDate from picklist editing) are preserved.
 */
export const mutationUpdateCustomerCart = {
  async updateCustomerCart(_parent, args, context, _info) {
    const { values, customerId, customerNumber, lang, cdn } = args
    const commerceApi = CommerceService.api()
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const customer = await getCustomer({ id: customerId, number: customerNumber })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const cart = await getActiveCustomerCart(customer.id, user.employeeNumber)

    if (!cart || cart.length <= 0) {
      throw new Error("Cart not found")
    }

    const storeKey = cart[0]?.store?.key

    // need to round because for example 4,90 is treated as 4,9000000000000006 in JS
    const freightCentAmount = Math.round((Number(values?.freightSurcharge) || 0) * 100)

    const freightSurchargeValue = {
      centAmount: freightCentAmount,
      type: "centPrecision",
      currencyCode: cart[0].totalPrice.currencyCode,
      fractionDigits: 2,
    }

    const payload = {
      ...(storeKey && { storeKey }),
      version: cart[0].version,
      actions: [
        {
          action: "setCustomField",
          name: "freightSurcharge",
          value: freightSurchargeValue,
        },
      ],
    }

    try {
      let updatedCart = (
        await commerceApi
          .carts()
          .withId({ ID: cart[0].id })
          .post({
            body: JSON.stringify(payload),
          })
          .execute()
      ).body

      const config = AppSettings.sanityConfigByLang(lang)
      const client = context.sanityClient
      client.config({ ...config, useCdn: cdn })

      return updatedCart
    } catch (e) {
      throw new Error(e)
    }
  },
}
